import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react'

import { api, isApiError, type ApiError } from '@/api/client'
import { ME_QUERY_KEY } from '@/api/queryClient'
import type { Me, RequestCodeResponse, SessionResponse, User, UserRole } from '@/api/types'

/**
 * Session state for the whole app.
 *
 * The server is the only authority on who the user is: there is no token in
 * `localStorage`, no decoded JWT, no role cached anywhere. `GET /api/auth/me`
 * is asked on mount and re-asked when the tab regains focus, and everything
 * below is derived from its answer. A 401 is not an error here — it is the
 * answer "nobody", and it is what puts the sign-in screen on screen.
 */

/** `loading` on first fetch; `error` only for failures that are *not* a 401. */
export type AuthStatus = 'loading' | 'authenticated' | 'anonymous' | 'error'

export interface AuthContextValue {
  status: AuthStatus
  /** The full principal, including scopes and API-key details. */
  me: Me | null
  /** The human behind the session. `null` for an API-key principal. */
  user: User | null
  role: UserRole | null
  isAuthenticated: boolean
  isAdmin: boolean
  isBd: boolean
  isAnalyst: boolean
  /** Server-supplied; the client never keeps its own role-to-scope map. */
  scopes: string[]
  hasScope: (scope: string) => boolean
  /** Set when `/api/auth/me` failed for a reason other than "not signed in". */
  error: ApiError | null

  requestCode: (email: string) => Promise<RequestCodeResponse>
  verify: (email: string, code: string) => Promise<SessionResponse>
  signOut: () => Promise<void>
  refresh: () => Promise<void>

  isRequestingCode: boolean
  isVerifying: boolean
  isSigningOut: boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

/**
 * Ask who the caller is.
 *
 * `null` means "not signed in". Any other failure is rethrown, because a
 * network outage and a rejected session are different problems and the shell
 * shows different screens for them.
 */
async function fetchMe(): Promise<Me | null> {
  try {
    return await api.get<Me>('/api/auth/me')
  } catch (error) {
    if (isApiError(error) && error.isUnauthenticated) return null
    throw error
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()

  const meQuery = useQuery<Me | null>({
    queryKey: ME_QUERY_KEY,
    queryFn: fetchMe,
    // A rejected session is a settled answer, not a flake.
    retry: (failureCount, error) => {
      if (isApiError(error) && !error.isNetworkError && error.status < 500) return false
      return failureCount < 2
    },
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  })

  const requestCodeMutation = useMutation<RequestCodeResponse, unknown, string>({
    mutationFn: (email: string) =>
      api.post<RequestCodeResponse>('/api/auth/request-code', { email: email.trim() }),
  })

  const verifyMutation = useMutation<SessionResponse, unknown, { email: string; code: string }>({
    mutationFn: ({ email, code }) =>
      api.post<SessionResponse>('/api/auth/verify', { email: email.trim(), code: code.trim() }),
    onSuccess: async () => {
      // The verify response carries the user but not the scopes, and scopes are
      // what the shell gates on — so re-ask rather than synthesising a `Me`.
      await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY })
    },
  })

  const signOutMutation = useMutation<void, unknown, void>({
    mutationFn: () => api.post<void>('/api/auth/logout'),
    onSettled: () => {
      // Even a failed logout clears the client: the cookie may well be gone,
      // and leaving the previous user's matters in the cache is worse than an
      // extra sign-in. `clear()` first, then seed the anonymous answer so the
      // shell does not flash a loading state on its way to SignIn.
      queryClient.clear()
      queryClient.setQueryData(ME_QUERY_KEY, null)
    },
  })

  const requestCode = useCallback(
    (email: string) => requestCodeMutation.mutateAsync(email),
    [requestCodeMutation],
  )

  const verify = useCallback(
    (email: string, code: string) => verifyMutation.mutateAsync({ email, code }),
    [verifyMutation],
  )

  const signOut = useCallback(async () => {
    await signOutMutation.mutateAsync()
  }, [signOutMutation])

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY })
  }, [queryClient])

  const value = useMemo<AuthContextValue>(() => {
    const me = meQuery.data ?? null
    const error = isApiError(meQuery.error) ? meQuery.error : null

    let status: AuthStatus
    if (meQuery.isPending) status = 'loading'
    else if (me) status = 'authenticated'
    else if (meQuery.isError) status = 'error'
    else status = 'anonymous'

    const role = me?.role ?? null
    const scopes = me?.scopes ?? []

    return {
      status,
      me,
      user: me?.user ?? null,
      role,
      isAuthenticated: status === 'authenticated',
      isAdmin: role === 'admin',
      isBd: role === 'bd',
      isAnalyst: role === 'analyst',
      scopes,
      hasScope: (scope: string) => scopes.includes(scope),
      error,
      requestCode,
      verify,
      signOut,
      refresh,
      isRequestingCode: requestCodeMutation.isPending,
      isVerifying: verifyMutation.isPending,
      isSigningOut: signOutMutation.isPending,
    }
  }, [
    meQuery.data,
    meQuery.error,
    meQuery.isPending,
    meQuery.isError,
    requestCode,
    verify,
    signOut,
    refresh,
    requestCodeMutation.isPending,
    verifyMutation.isPending,
    signOutMutation.isPending,
  ])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// The hook is the provider's public surface; a separate module for one
// `useContext` call would only add an import, so fast refresh loses this file.
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used inside <AuthProvider>.')
  }
  return context
}
